import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ContractDeletedWarning } from '@app/features/contracts/contract-deleted-warning/contract-deleted-warning';
import { ContractListItem } from '@app/features/contracts/contract.model';
import { AMENDMENT_ORIGIN } from '@app/features/contracts/history/contract-amendments-history';
import { provideContractHistorySources } from '@app/features/contracts/history/contract-history-sources';
import { HistoryEntryView } from '@app/features/contracts/history/history-entry-view/history-entry-view';
import {
  CONTRACT_HISTORY_SOURCES,
  HistoryEntry,
  HistoryOrigin,
} from '@app/features/contracts/history/history-entry.model';
import { UserIdToUserNamePipe } from '@app/features/users/user-id-to-user-name.pipe';
import { LabelValue } from '@app/shared/forms/label-value/label-value';
import { catchError, combineLatest, map, of, startWith, switchMap } from 'rxjs';

export interface HistoryFilter {
  /** `null` pour « Tout ». */
  readonly key: string | null;
  readonly label: string;
  readonly count: number;
}

const time = (entry: HistoryEntry) => Date.parse(entry.at);

/** Une entrée relève de son origine, et de celle de son sujet : un avenant, quelle qu'elle soit. */
const belongsTo = (entry: HistoryEntry, key: string) =>
  entry.origin.key === key || entry.subject?.origin.key === key;

/**
 * Historique d'un contrat : un fil daté unique, réuni depuis toutes les sources fournies sous
 * `CONTRACT_HISTORY_SOURCES` et filtrable par origine. Ce qui est à venir se tient en tête. Les
 * entrées d'un avenant s'y mêlent à celles de l'embauche, marquées de leur avenant.
 */
@Component({
  selector: 'app-contract-event-history',
  imports: [DatePipe, LabelValue, UserIdToUserNamePipe, ContractDeletedWarning, HistoryEntryView],
  providers: [provideContractHistorySources()],
  templateUrl: './contract-event-history.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './contract-event-history.scss',
})
export class ContractEventHistory {
  readonly contract = input.required<ContractListItem>();

  private readonly sources = inject(CONTRACT_HISTORY_SOURCES);

  private readonly entries = toSignal(
    toObservable(this.contract).pipe(
      switchMap((contract) =>
        combineLatest(
          this.sources.map((source) =>
            source.entries(contract).pipe(
              // Une source en échec (l'erreur est déjà signalée globalement) ne vide pas le fil.
              catchError(() => of<HistoryEntry[]>([])),
              startWith<HistoryEntry[]>([]),
            ),
          ),
        ),
      ),
      map((lists) => lists.flat()),
    ),
    { initialValue: [] },
  );

  private readonly origins: HistoryOrigin[] = this.sources
    .map((source) => source.origin)
    .filter((origin, i, all) => all.findIndex((o) => o.key === origin.key) === i);

  readonly selected = signal<string | null>(null);

  /** L'origine des avenants, dont le filtre et les entrées portent une ancre de parcours. */
  readonly amendmentKey = AMENDMENT_ORIGIN.key;

  readonly filters = computed<HistoryFilter[]>(() => {
    const entries = this.entries();
    return [
      { key: null, label: 'Tout', count: entries.length },
      ...this.origins.map((origin) => ({
        key: origin.key,
        label: origin.label,
        count: entries.filter((entry) => belongsTo(entry, origin.key)).length,
      })),
    ];
  });

  private readonly visible = computed(() => {
    const selected = this.selected();
    const entries = this.entries();
    return selected === null ? entries : entries.filter((entry) => belongsTo(entry, selected));
  });

  readonly upcoming = computed(() =>
    this.visible()
      .filter((entry) => entry.upcoming)
      .sort((a, b) => time(a) - time(b)),
  );

  readonly past = computed(() =>
    this.visible()
      .filter((entry) => !entry.upcoming)
      .sort((a, b) => time(b) - time(a)),
  );

  /** Les précisions des origines présentes dans le fil. */
  readonly notes = computed(() =>
    this.origins
      .filter((origin) => origin.note)
      .filter((origin) => this.entries().some((entry) => entry.origin.key === origin.key))
      .map((origin) => origin.note!),
  );

  select(key: string | null): void {
    this.selected.set(key);
  }
}
